import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

export function Cartao({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-2xl bg-white p-5 shadow-cartao ${className}`}>{children}</div>
  );
}

export function Campo({
  rotulo,
  erro,
  ...props
}: { rotulo: string; erro?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-tinta-700">{rotulo}</span>
      <input
        {...props}
        className={`w-full rounded-xl border bg-white px-3.5 py-2.5 text-tinta-900 outline-none transition
          placeholder:text-tinta-400/70 focus:border-tinta-900 ${
            erro ? 'border-red-400 bg-red-50' : 'border-tinta-200'
          } ${props.className ?? ''}`}
      />
      {erro && <span className="mt-1 block text-sm text-red-600">{erro}</span>}
    </label>
  );
}

export function Botao({
  carregando,
  children,
  variante = 'principal',
  ...props
}: {
  carregando?: boolean;
  variante?: 'principal' | 'secundario' | 'suave';
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const estilo = {
    principal: 'bg-tinta-900 text-white hover:bg-tinta-700 disabled:bg-tinta-400',
    secundario: 'border border-tinta-200 bg-white text-tinta-700 hover:bg-tinta-100',
    suave: 'bg-tinta-100 text-tinta-700 hover:bg-tinta-200',
  }[variante];
  return (
    <button
      {...props}
      disabled={props.disabled || carregando}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed ${estilo} ${props.className ?? ''}`}
    >
      {carregando && (
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
      )}
      {children}
    </button>
  );
}

export function Alerta({
  tipo = 'erro',
  children,
}: {
  tipo?: 'erro' | 'aviso' | 'ok';
  children: ReactNode;
}) {
  const cores = {
    erro: 'bg-red-50 text-red-700 ring-red-100',
    aviso: 'bg-amber-50 text-amber-800 ring-amber-100',
    ok: 'bg-tinta-100 text-tinta-900 ring-tinta-200',
  }[tipo];
  return <div className={`rounded-xl px-3.5 py-2.5 text-sm ring-1 ${cores}`}>{children}</div>;
}

export function Etiqueta({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <span className={`rounded-lg px-2 py-0.5 text-xs font-semibold ${className}`}>{children}</span>
  );
}

/** Compara ignorando acento e caixa: quem digita "profetico" acha "Profético". */
function achatar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Escolha de um item numa lista que cresceu demais para caber em botoes lado a
 * lado. Abre uma lista com campo de busca, do jeito que se procura uma conta
 * numa ferramenta de anuncios.
 */
export function SeletorBusca({
  opcoes,
  valor,
  aoEscolher,
  rotulo,
  vazio = 'Nada encontrado.',
  className = '',
}: {
  opcoes: { valor: string; rotulo: string }[];
  valor: string;
  aoEscolher: (valor: string) => void;
  rotulo: string;
  vazio?: string;
  className?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const caixa = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLInputElement>(null);

  const escolhida = opcoes.find((o) => o.valor === valor) ?? null;

  const filtradas = useMemo(() => {
    const termo = achatar(busca.trim());
    if (!termo) return opcoes;
    return opcoes.filter((o) => achatar(o.rotulo).includes(termo));
  }, [opcoes, busca]);

  // clicar fora e Esc fecham; abrir sempre comeca com a busca limpa e no cursor
  useEffect(() => {
    if (!aberto) return;
    setBusca('');
    campo.current?.focus();
    const foraDaCaixa = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    const teclou = (e: KeyboardEvent) => e.key === 'Escape' && setAberto(false);
    document.addEventListener('mousedown', foraDaCaixa);
    document.addEventListener('keydown', teclou);
    return () => {
      document.removeEventListener('mousedown', foraDaCaixa);
      document.removeEventListener('keydown', teclou);
    };
  }, [aberto]);

  return (
    <div ref={caixa} className={`relative ${className}`}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={aberto}
        aria-label={rotulo}
        onClick={() => setAberto((a) => !a)}
        className="flex w-full items-center justify-between gap-3 rounded-xl bg-white px-4 py-2.5 text-left text-sm font-semibold text-tinta-900 shadow-cartao transition hover:bg-tinta-100"
      >
        <span className="truncate">{escolhida?.rotulo ?? rotulo}</span>
        <span aria-hidden className="shrink-0 text-xs text-tinta-400">
          &#9662;
        </span>
      </button>

      {aberto && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-xl border border-tinta-200 bg-white shadow-cartao">
          <div className="border-b border-tinta-200 p-2">
            <input
              ref={campo}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || !filtradas[0]) return;
                aoEscolher(filtradas[0].valor);
                setAberto(false);
              }}
              placeholder="Procurar..."
              aria-label={`Procurar ${rotulo.toLowerCase()}`}
              className="w-full rounded-lg border border-tinta-200 px-3 py-1.5 text-sm outline-none transition placeholder:text-tinta-400/70 focus:border-tinta-900"
            />
          </div>
          <ul role="listbox" className="max-h-72 overflow-y-auto py-1">
            {filtradas.length === 0 && <li className="px-3 py-2 text-sm text-tinta-400">{vazio}</li>}
            {filtradas.map((o) => (
              <li key={o.valor}>
                <button
                  type="button"
                  role="option"
                  aria-selected={o.valor === valor}
                  onClick={() => {
                    aoEscolher(o.valor);
                    setAberto(false);
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition ${
                    o.valor === valor
                      ? 'bg-tinta-900 font-semibold text-white'
                      : 'text-tinta-700 hover:bg-tinta-100'
                  }`}
                >
                  <span className="w-3 shrink-0" aria-hidden>
                    {o.valor === valor ? '\u2713' : ''}
                  </span>
                  <span className="truncate">{o.rotulo}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Ancora com cara de botao. Baixar documento e navegacao, nao acao: um
 * <button> dentro de <a> e HTML invalido e o clique pode nao navegar.
 */
export function LinkBotao({
  children,
  variante = 'secundario',
  className = '',
  ...props
}: {
  variante?: 'principal' | 'secundario' | 'suave';
} & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const estilo = {
    principal: 'bg-tinta-900 text-white hover:bg-tinta-700',
    secundario: 'border border-tinta-200 bg-white text-tinta-700 hover:bg-tinta-100',
    suave: 'bg-tinta-100 text-tinta-700 hover:bg-tinta-200',
  }[variante];
  return (
    <a
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${estilo} ${className}`}
    >
      {children}
    </a>
  );
}

/** Janela sobre a tela. Clicar fora fecha — a mesma em toda a administracao. */
export function Modal({
  titulo,
  children,
  aoFechar,
}: {
  titulo: string;
  children: ReactNode;
  aoFechar: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-tinta-900/30 p-4"
      onClick={aoFechar}
    >
      <div className="my-auto w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <Cartao>
          <h3 className="text-lg font-semibold text-tinta-900">{titulo}</h3>
          {children}
        </Cartao>
      </div>
    </div>
  );
}
